'use strict';

ObjectsGridGridFactory.$inject = [
	'mx.SolutionBuilderAgent.BaseGrid',
	'$q',
	'mx.internationalization',
	'mx.SolutionBuilderAgent.DialogOpenerService',
	'mx.shell.ActionManagerService',
	'mx.shell.ActionExecutorService',
	'mx.SolutionBuilderAgent.MultiSelectionTabService',
	'mx.SolutionBuilderAgent.MxGridHelperService',
	'mx.SolutionBuilderAgent.GridEnums',
];
export default function ObjectsGridGridFactory(
	BaseGrid,
	$q,
	i18n,
	dialogOpenerService,
	actionManagerService,
	actionExecutorService,
	multiSelectionTabService,
	gridHelperService,
	GridEnums
) {
	const { EditMode, ObjectGridOnClickActions, DataDefinitionGridTypes } = GridEnums;

	class ObjectsGrid extends BaseGrid {
		constructor() {
			super();
			this.gridValidationMessage = i18n.get('components.form-validation.required');
		}

		initProperties() {
			super.initProperties();
			let objectIDs,
				deleteActionIds,
				filter,
				allowedTypes,
				clickAction = ObjectGridOnClickActions.Preview,
				dblClickAction = ObjectGridOnClickActions.Edit;

			Object.defineProperties(this, {
				objectIDs: {
					configurable: true,
					get: function () {
						return objectIDs;
					},
				},
				data: {
					configurable: true,
					set: function (newValue) {
						if (Array.isArray(newValue)) {
							objectIDs = newValue;
							if (this.executor) {
								this.executor.updateRequestOptions({
									objectIDs: newValue,
								});
								this.debouncedLoadItems();
							}
						} else {
							objectIDs = [];
						}
					},
				},
				filter: {
					configurable: true,
					get: function () {
						return filter;
					},
					set: function (newValue) {
						filter = newValue;
						this.updateRequest();
						this.debouncedLoadItems();
					},
				},
				entityClassName: {
					configurable: true,
					writable: true,
				},
				allowedTypes: {
					configurable: true,
					get: function () {
						return allowedTypes;
					},
					set: async function (newValue) {
						if (allowedTypes !== newValue) {
							allowedTypes = newValue;
							this.createItems = await this.initCreateButtonItems();
							this.updateRequest();
							this.debouncedLoadItems();
						}
					},
				},
				columnDefinition: {
					writable: true,
				},
				browseWhereClause: {
					configurable: true,
					writable: true,
				},
				deleteActionIds: {
					configurable: true,
					get: function () {
						return deleteActionIds;
					},
					set: function (newValue) {
						//TODO: multi-object-picker doesn't support 'useObjectId', so it is possible to use only 1 action instead of array.
						if (newValue) {
							deleteActionIds = [newValue];
							actionManagerService.getActionsByIds(deleteActionIds).then(
								function (actions) {
									this.deleteActions = actions;
									this.deleteButtonHidden = actions.length === 0;
								}.bind(this)
							);
						}
					},
				},
				onlyTypedSelectSearch: {
					writable: true,
					value: false,
				},
				editMode: {
					configurable: true,
					get: function () {
						return EditMode.GenericEdit; // open Preview on click
					},
					set: function () {},
				},
				inlineMode: {
					configurable: true,
					get: function () {
						return false; // inline mode is disabled for object grids
					},
					set: function () {},
				},
				clickAction: {
					configurable: true,
					get: function () {
						return clickAction;
					},
					set: function (newValue) {
						if (ObjectGridOnClickActions.containsValue(newValue)) {
							clickAction = newValue;
						}
					},
				},
				dblClickAction: {
					configurable: true,
					get: function () {
						return dblClickAction;
					},
					set: function (newValue) {
						if (ObjectGridOnClickActions.containsValue(newValue)) {
							dblClickAction = newValue;
						}
					},
				},
			});
		}

		async initDataQuery() {
			return gridHelperService.initDQByDefinition(this.columnDefinition, this.entityClassName, DataDefinitionGridTypes.Objects);
		}

		updateRequest() {
			if (this.executor) {
				this.executor.updateRequestOptions({
					entityClassName: this.entityClassName,
					archived: this.parentView?.context?._archived || false,
					objectIDs: this.objectIDs,
					excludedIDs: this.excludedIDs,
					allowedTypes: this.allowedTypes,
					correctEntityFilter: this.getCorrectEntityFilter(),
					filter: this.filter,
					columnDefinition: this.columnDefinition,
				});
			}
		}

		getCorrectEntityFilter() {
			return; //TODO: here should be used this.filter, but legacy filters (relation-filters configured in designer for object-grid) can fail creating new items
		}

		async initCreateButtonItems() {
			if (!this.executor) {
				return [];
			}
			let types = this.executor.allEntityTypes;
			if (this.allowedTypes && this.allowedTypes.length) {
				const allowedTypes = this.allowedTypes.split(',').map((type) => type.trim());
				types = this.executor.allEntityTypes.filter((type) => allowedTypes.includes(type._type));
			}

			return await gridHelperService.getEntityTypesForCreate(types);
		}

		findExistingEntities() {
			return multiSelectionTabService.open(
				{
					title: this.title,
					multiSelection: true,

					entityClassName: this.entityClassName,
					allowedTypes: this.allowedTypes,
					filter: this.filter,
					correctEntityFilter: this.browseWhereClause,
					excludedIDs: this.objectIDs,
					columnDefinition: this.columnDefinition,
					onlyTypedSelectSearch: this.onlyTypedSelectSearch,
					presetFields: this.getPresetFields(),
					createButtonHidden: this.createButtonInSelectionHidden,
				},
				this.parentView
			);
		}

		getPresetFields() {
			return {};
		}

		afterEntitiesCreated(entities) {
			var newIds = entities
				.map(function (entity) {
					return entity.Id;
				})
				.filter(function (fragmentId) {
					return this.objectIDs.indexOf(fragmentId) === -1;
				}, this);

			this.objectIDs.push.apply(this.objectIDs, newIds);
		}

		deleteItems(items) {
			if (!Array.isArray(items) || !items.length || !this.deleteAction) {
				return $q.reject();
			}

			return actionExecutorService.execute(this.deleteAction, items, this.parentView);
		}

		removeEntities(entities) {
			var removedFragmentIds = entities.map(function (entity) {
				return entity.Id;
			});

			mx.components.Utils.removeItemsFromArray(this.objectIDs, removedFragmentIds);

			this.updateRequest();
			return this.loadItems();
		}

		selectionHandler(data) {
			super.selectionHandler(data);
			if (this.deleteButtonHidden) {
				return;
			}
			const actions = actionManagerService.filterActions(this.deleteActions, null, data.allSelectedEntities);
			if (actions.length === 1) {
				this.deleteAction = actions[0];
				this.deleteButtonDisabled = false;
			} else {
				this.deleteButtonDisabled = true;
			}
		}

		reorderHandler() {
			var ids = this.executor.dataItems.map(function (row) {
				return row.getEntity().Id; //fragmentId
			});

			this.objectIDs.sort(function (idA, idB) {
				var indexA = ids.indexOf(idA);
				var indexB = ids.indexOf(idB);
				if (indexA < 0 || indexB < 0) {
					return 0;
				}
				return indexA > indexB ? 1 : -1;
			});

			this.updateRequest();
		}

		itemClickHandler(row) {
			return this._doClickAction(row, this.clickAction);
		}

		itemDblClickHandler(row) {
			return this._doClickAction(row, this.dblClickAction);
		}

		_doClickAction(row, clickAction) {
			this.currentRow = row;
			switch (clickAction) {
				case ObjectGridOnClickActions.Preview:
					this.runGenericPreview(row.getEntity());
					break;
				case ObjectGridOnClickActions.Edit:
					this.editRow(row).catch(mx.logCatch);
					break;
			}
		}
	}

	return ObjectsGrid;
}
